import React, { useState } from 'react';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useTeacherAvatar } from '../../hooks/useTeacherAvatar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DndContext, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Brain, Play, RotateCcw, Star, Trash2, Target, Info, Zap,
  Sparkles, MessageSquare, Plus, Loader2, Copy, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOCK_CATEGORIES = [
  {
    id: 'role',
    label: 'Role',
    color: '#8b5cf6',
    blocks: [
      { id: 'role-expert', text: 'You are an expert' },
      { id: 'role-teacher', text: 'You are a friendly teacher' },
      { id: 'role-coder', text: 'You are a senior developer' },
      { id: 'role-writer', text: 'You are a creative writer' },
      { id: 'role-scientist', text: 'You are a research scientist' },
    ]
  },
  {
    id: 'task',
    label: 'Task',
    color: '#3b82f6',
    blocks: [
      { id: 'task-explain', text: 'Explain the concept of' },
      { id: 'task-write', text: 'Write a short paragraph about' },
      { id: 'task-list', text: 'List 5 key points about' },
      { id: 'task-compare', text: 'Compare and contrast' },
      { id: 'task-summarize', text: 'Summarize in simple terms:' },
      { id: 'task-code', text: 'Write a code example for' },
    ]
  },
  {
    id: 'context',
    label: 'Context',
    color: '#10b981',
    blocks: [
      { id: 'ctx-beginner', text: 'for a 10-year-old student' },
      { id: 'ctx-college', text: 'for a college student' },
      { id: 'ctx-professional', text: 'for a professional audience' },
      { id: 'ctx-fun', text: 'in a fun and engaging way' },
      { id: 'ctx-practical', text: 'with practical real-world examples' },
    ]
  },
  {
    id: 'format',
    label: 'Format',
    color: '#f59e0b',
    blocks: [
      { id: 'fmt-bullet', text: 'Use bullet points' },
      { id: 'fmt-steps', text: 'Format as numbered steps' },
      { id: 'fmt-table', text: 'Present as a table' },
      { id: 'fmt-story', text: 'Tell it as a short story' },
      { id: 'fmt-short', text: 'Keep it under 100 words' },
    ]
  },
  {
    id: 'constraint',
    label: 'Constraint',
    color: '#ef4444',
    blocks: [
      { id: 'con-no-jargon', text: 'Avoid technical jargon' },
      { id: 'con-examples', text: 'Include at least 2 examples' },
      { id: 'con-analogy', text: 'Use a creative analogy' },
      { id: 'con-emoji', text: 'Include relevant emojis' },
      { id: 'con-question', text: 'End with a thought-provoking question' },
    ]
  },
];

const MISSIONS = [
  { id: 1, name: 'Hello AI', description: 'Build a prompt with at least 2 blocks and get a response', minBlocks: 2, stars: 1,
    sample: 'Try: "You are a friendly teacher" + "Explain the concept of" + your topic' },
  { id: 2, name: 'Structured Prompt', description: 'Use blocks from 3 different categories', minCategories: 3, stars: 2,
    sample: 'Try: Role + Task + Context. E.g., "You are an expert" + "List 5 key points about" + "for a 10-year-old student"' },
  { id: 3, name: 'Master Prompter', description: 'Use blocks from all 5 categories', minCategories: 5, stars: 3,
    sample: 'Use one block from each: Role + Task + Context + Format + Constraint. E.g., "You are a research scientist" + "Compare and contrast" + topic + "Present as a table" + "Include at least 2 examples"' },
  { id: 4, name: 'Custom Creator', description: 'Add a custom topic and test your prompt', needsCustom: true, stars: 2,
    sample: 'Type a topic like "photosynthesis" or "Indian history" in the text box, then add blocks to shape the AI response' },
  { id: 5, name: 'Constraint Master', description: 'Build a prompt using at least 2 constraint blocks', minConstraints: 2, stars: 2,
    sample: 'Try: Role + Task + two constraints like "Avoid technical jargon" + "Include at least 2 examples"' },
  { id: 6, name: 'Format Expert', description: 'Build a prompt that uses a specific format block (table, steps, or bullet points)', needsFormat: true, stars: 2,
    sample: 'E.g., "You are an expert" + "Compare and contrast" + topic + "Present as a table"' },
  { id: 7, name: 'Chain Prompting', description: 'Build two related prompts where the second builds on the first response', isChain: true, stars: 3,
    sample: 'First: "Explain photosynthesis in simple terms" → Then: "Now create a quiz based on the explanation above"' },
  { id: 8, name: 'Audience Adapter', description: 'Build the same prompt for two different audiences (beginner vs professional)', needsDualAudience: true, stars: 3,
    sample: 'Same topic but: Version 1 with "for a 10-year-old" vs Version 2 with "for a professional audience"' },
  { id: 9, name: 'Minimal Prompt', description: 'Get a useful response using only 1 block plus a custom topic (less is more!)', maxBlocks: 1, needsCustom: true, stars: 2,
    sample: 'Use just one powerful block like "List 5 key points about" + your topic' },
  { id: 10, name: 'Mega Prompt', description: 'Build the most detailed prompt possible using 6+ blocks from all categories', minBlocks: 6, minCategories: 5, stars: 3,
    sample: 'Combine all categories: Role + Task + Context + Format + Constraint + extra blocks for maximum control' },
];

function PaletteBlock({ block, category }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${block.id}`,
    data: { type: 'block', block, category },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-grab active:cursor-grabbing border select-none transition-transform ${isDragging ? 'opacity-30' : 'hover:scale-105'}`}
      style={{
        borderColor: category.color + '40',
        background: category.color + '15',
        color: category.color,
      }}
      data-testid={`block-${block.id}`}
    >
      {block.text}
    </div>
  );
}

function PromptZone({ children, isEmpty }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'prompt-zone' });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[120px] rounded-xl border-2 border-dashed p-3 transition-colors ${
        isOver ? 'border-primary/60 bg-primary/5' : 'border-border/50 bg-card/30'
      }`}
      data-testid="prompt-zone"
    >
      {isEmpty && (
        <p className="text-muted-foreground/40 text-sm text-center py-6">Drag blocks here to build your prompt</p>
      )}
      {children}
    </div>
  );
}

export default function AIPromptLab() {
  const { showTeacher, dismiss, resummon, dismissed, avatarId } = useTeacherAvatar('ai-prompt');
  const navigate = useNavigate();
  const [promptBlocks, setPromptBlocks] = useState([]);
  const [customTopic, setCustomTopic] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('ai-prompt', 10);
  const [activeDrag, setActiveDrag] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const mission = MISSIONS[currentMission];

  const handleDragStart = (event) => {
    const { active } = event;
    if (active.data.current?.type === 'block') {
      setActiveDrag(active.data.current);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDrag(null);

    if (over?.id === 'prompt-zone' && active.data.current?.type === 'block') {
      const { block, category } = active.data.current;
      // Don't add duplicates
      if (!promptBlocks.find(b => b.block.id === block.id)) {
        setPromptBlocks(prev => [...prev, { block, category }]);
      }
    }
  };

  const removeBlock = (blockId) => {
    setPromptBlocks(prev => prev.filter(b => b.block.id !== blockId));
  };

  const clearPrompt = () => {
    setPromptBlocks([]);
    setAiResponse('');
    setCustomTopic('');
    setError('');
  };

  const getComposedPrompt = () => {
    const parts = promptBlocks.map(b => b.block.text);
    if (customTopic.trim()) {
      parts.push(customTopic.trim());
    }
    return parts.join(' ');
  };

  const testPrompt = async () => {
    const prompt = getComposedPrompt();
    if (!prompt.trim()) return;

    setIsLoading(true);
    setAiResponse('');
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/labs/ai-prompt/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ prompt }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to get AI response');
      }

      const data = await res.json();
      setAiResponse(data.response);

      // Check mission completion
      checkMissionCompletion();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const checkMissionCompletion = () => {
    const m = mission;
    let completed = false;

    if (m.minBlocks && promptBlocks.length >= m.minBlocks) {
      completed = true;
    }
    if (m.minCategories) {
      const uniqueCats = new Set(promptBlocks.map(b => b.category.id));
      completed = uniqueCats.size >= m.minCategories;
    }
    if (m.needsCustom) {
      completed = customTopic.trim().length > 0 && promptBlocks.length >= 1;
    }

    if (completed && !completedMissions.includes(currentMission)) {
      saveProgress(currentMission);
    }
  };

  const copyPrompt = () => {
    navigator.clipboard.writeText(getComposedPrompt());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const uniqueCategories = new Set(promptBlocks.map(b => b.category.id));

  return (
    <div className="min-h-screen bg-background" data-testid="ai-prompt-lab">
      <TeacherAvatar visible={showTeacher} avatarId={avatarId}
        text="AI lab about prompt engineering. Build prompts visually with drag-and-drop blocks and test them against a real AI model."
        cacheKey="lab-intro-ai-prompt"
        onClose={dismiss} dismissed={dismissed} onResummon={resummon} />
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              <span className="font-semibold">AI Prompt Engineering Lab</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {MISSIONS.map((m, i) => (
              <Badge
                key={m.id}
                variant={completedMissions.includes(i) ? 'default' : currentMission === i ? 'secondary' : 'outline'}
                className={`cursor-pointer text-xs ${currentMission === i ? 'ring-2 ring-primary/50' : ''}`}
                onClick={() => { clearPrompt(); setCurrentMission(i); }}
                data-testid={`mission-${m.id}`}
              >
                {completedMissions.includes(i) ? <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" /> : null}
                M{m.id}
              </Badge>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Mission info */}
        <motion.div
          key={currentMission}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-purple-500/10 border border-purple-500/20"
        >
          <Target className="w-5 h-5 text-purple-400 shrink-0" />
          <div className="flex-1">
            <div><span className="font-semibold text-sm">{mission.name}:</span>
            <span className="text-sm text-muted-foreground ml-2">{mission.description}</span></div>
            {mission.sample && (
              <p className="text-xs text-purple-300/70 mt-1 italic" data-testid="mission-sample">{mission.sample}</p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{uniqueCategories.size}/5 categories</span>
            <div className="flex gap-1">
              {[...Array(mission.stars)].map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${completedMissions.includes(currentMission) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
              ))}
            </div>
          </div>
        </motion.div>

        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          {/* Block Palette - above drop zone for easier drag */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-yellow-400" /> Block Palette — Drag blocks into the builder below
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {BLOCK_CATEGORIES.map(cat => (
                <div key={cat.id} className="p-3 rounded-xl bg-card border border-border/50">
                  <h4 className="text-xs font-semibold mb-2" style={{ color: cat.color }}>
                    {cat.label}
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.blocks.map(block => (
                      <PaletteBlock key={block.id} block={block} category={cat} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Prompt Builder + AI Response */}
          <div className="space-y-4 max-w-4xl">
            {/* Prompt builder drop zone */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" /> Prompt Builder
                </h3>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={copyPrompt}>
                    {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={clearPrompt}>
                    <Trash2 className="w-3 h-3 mr-1" /> Clear
                  </Button>
                </div>
              </div>

              <PromptZone isEmpty={promptBlocks.length === 0}>
                <div className="flex flex-wrap gap-2">
                  <AnimatePresence>
                    {promptBlocks.map(({ block, category }) => (
                      <motion.div
                        key={block.id}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-2 group"
                        style={{
                          borderColor: category.color + '40',
                          background: category.color + '15',
                          color: category.color,
                        }}
                      >
                        {block.text}
                        <button
                          onClick={() => removeBlock(block.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </PromptZone>

              {/* Custom topic */}
              <div className="mt-3">
                <Textarea
                  value={customTopic}
                  onChange={e => setCustomTopic(e.target.value)}
                  placeholder="Add your custom topic or details here (optional)..."
                  className="min-h-[60px] text-sm"
                  data-testid="custom-topic-input"
                />
              </div>

              {/* Composed prompt preview */}
              {(promptBlocks.length > 0 || customTopic) && (
                <div className="mt-3 p-3 rounded-xl bg-muted/30 border border-border/30">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-1">Composed Prompt:</h4>
                  <p className="text-sm">{getComposedPrompt()}</p>
                </div>
              )}

              {/* Test button */}
              <div className="mt-4 flex gap-2">
                <Button
                  onClick={testPrompt}
                  disabled={isLoading || (promptBlocks.length === 0 && !customTopic.trim())}
                  className="gap-2"
                  data-testid="test-prompt-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  {isLoading ? 'Thinking...' : 'Test with AI'}
                </Button>
              </div>
            </div>

            {/* AI Response */}
            <AnimatePresence>
              {(aiResponse || error) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`p-4 rounded-xl border ${error ? 'bg-red-500/10 border-red-500/20' : 'bg-card border-border/50'}`}
                >
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    {error ? 'Error' : 'AI Response'}
                  </h4>
                  {error ? (
                    <p className="text-sm text-red-400">{error}</p>
                  ) : (
                    <div className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="ai-response">
                      {aiResponse}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <DragOverlay>
            {activeDrag ? (
              <div
                className="px-3 py-1.5 rounded-lg text-xs font-medium border opacity-80"
                style={{
                  borderColor: activeDrag.category.color + '40',
                  background: activeDrag.category.color + '15',
                  color: activeDrag.category.color,
                }}
              >
                {activeDrag.block.text}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}
